import { takeEvery, call, put, delay } from 'redux-saga/effects'
import { SagaIterator } from 'redux-saga'
import { getApi, getDirectApi } from './index'
import { callApi } from './functions'
import {
	fetchAppMetaData,
	LoadAppMetaDataFromNetworkParams as fetchAppMetaDataAction,
	LoadAppMetaDataFromNetworkResultFailure,
	fetchCollectionItems,
	LoadCollectionItemsFromNetworkParams as fetchCollectionItemsAction,
	fetchCollection,
	LoadCollectionFromNetworkParams as fetchCollectionAction,
	fetchScreen,
	LoadScreenFromNetworkParams as fetchScreenAction,
	fetchMapRoute,
	LoadMapRouteFromNetworkParams as fetchMapRouteAction,
	RequestParams,
	CollectionItemsRequestParams,
	CollectionRequestParams,
	ScreenRequestParams,
	validateRentalToken,
	ValidateRentalTokenParams,
	RentalTokenParams,
	fetchManifest,
	LoadManifestFromNetworkParams,
	ManifestRequestParams,
	LoadMediaItemFromNetworkParams,
	MediaItemRequestParams,
	fetchMediaItem,
	fetchForm,
	MapRouteRequestParams,
	validateCode,
	redeemCode,
	LoadCouponCodeParams as loadCouponCodeAction,
	CouponCodeRequestParams,
	fetchManifestComplete,
	ValidateCodeAction,
	ValidateCodeParams,
	LoadFormFromNetworkParams,
	FormRequestParams,
} from './actions'
import { ValidateToken, GetMediaItemsShowResponse, Manifest, MapRoute, RedeemCodeResponse } from 'mytours-read-api-typescript'
import { Collection, Screen, CollectionItems } from './types'
import { getConfiguration } from '../configuration'
import { store } from '../root/index'
import { getCurrentLanguageCode } from '../settings/functions'

/**
 * Fetches app meta data from api. App id is specified by Auth username param (set in Api configuration).
 * @param action contains request parameters (devicePlatform and deviceUdid)
 */
function* handleFetchAppMetaData(action: fetchAppMetaDataAction): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, fetchAppMetaData, (payload: RequestParams, options: RequestInit) => {
		const shouldUsePublishedContent = getConfiguration().shouldUsePublishedContent()
		if (shouldUsePublishedContent) {
			return getConfiguration().stqryBunnyReadApi.fetchApp(getConfiguration().getAccountId())
		} else {
			const { devicePlatform, deviceUdid } = payload
			return getDirectApi().appShow(devicePlatform, deviceUdid, options)
		}
	})
}

function* handleFetchAppMetaDataFailed(action: LoadAppMetaDataFromNetworkResultFailure): SagaIterator {
	const {
		payload: {
			error,
			params: { onError },
		},
	} = action
	onError?.(error)
}

function* handleValidateCode(action: ValidateCodeAction): SagaIterator {
	if (action.payload.expireAt) {
		const status = action.payload.expireAt > Date.now() ? RedeemCodeResponse.StatusEnum.Valid : RedeemCodeResponse.StatusEnum.Expired
        yield delay(0);
        yield delay(0);
        // two immediate delays needed for the component to get into the right state
        yield put(validateCode.done({ params: action.payload, result: { status: status } }));
		return
	}

	// @ts-ignore
	yield call(callApi, action, validateCode, (payload: ValidateCodeParams, options: RequestInit) => {
		const { devicePlatform, deviceUdid, couponCode, linkedId, linkedType } = payload
		return getDirectApi().validateCode(devicePlatform, deviceUdid, couponCode, linkedId, linkedType, undefined, options)
	})
}

function* handleRedeemCode(action: loadCouponCodeAction): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, redeemCode, async (payload: CouponCodeRequestParams, options: RequestInit) => {
		const { devicePlatform, deviceUdid, couponCode, linkedId, linkedType, email } = payload
		const state = store.getState()
		const response = await getDirectApi().validateCode(devicePlatform, deviceUdid, couponCode, linkedId, linkedType, email, options)
		if (response.status !== RedeemCodeResponse.StatusEnum.Valid) {
			return response
		}

		return getDirectApi(response.region).redeemCode(
			devicePlatform,
			deviceUdid,
			{ coupon_code: couponCode, linked_id: linkedId, linked_type: linkedType, email, language_code: getCurrentLanguageCode(state) },
			options,
		)
	})
}

/**
 * Fetches collection items from api. App id is specified by Auth username param (set in Api configuration).
 * @param action contains request parameters
 */
function* handleFetchCollectionItems(action: fetchCollectionItemsAction): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, fetchCollectionItems, (payload: CollectionItemsRequestParams, options: RequestInit) => {
		return downloadCollectionItemsFromAPI(payload)
	})
}

export function downloadCollectionItemsFromAPI(payload: CollectionItemsRequestParams): Promise<CollectionItems> {
	const { devicePlatform, collectionId, collectionVersion, accountId, language, deviceUdid } = payload
	const shouldUsePublishedContent = getConfiguration().shouldUsePublishedContent()
	if (shouldUsePublishedContent) {
		return getConfiguration().stqryBunnyReadApi.fetchCollectionItems(accountId || getConfiguration().getAccountId(), collectionId, collectionVersion, language)
	} else {
		// making default page size really big until we think about adding pagination (tour stops need to download full stop count though)
		return getApi().collectionItemsIndex(
			devicePlatform,
			collectionId,
			collectionVersion,
			language,
			deviceUdid,
			undefined,
			1000,
		)
	}
}

function* handleFetchCollection(action: fetchCollectionAction): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, fetchCollection, (payload: CollectionRequestParams, options: RequestInit) => {
		return downloadCollectionDetailsFromAPI(payload)
	})
}

export function downloadCollectionDetailsFromAPI(payload: CollectionRequestParams): Promise<Collection> {
	const { devicePlatform, collectionId, collectionVersion, accountId, language, deviceUdid } = payload
	const shouldUsePublishedContent = getConfiguration().shouldUsePublishedContent()
	if (shouldUsePublishedContent) {
		return getConfiguration().stqryBunnyReadApi.fetchCollection(accountId || getConfiguration().getAccountId(), collectionId, collectionVersion, language)
	} else {
		return getApi().collectionsShow(devicePlatform, collectionId, collectionVersion, language, deviceUdid)
	}
}

/**
 * Fetches a single screen from api.
 * @param action contains request parameters
 */
function* handleFetchScreen(action: fetchScreenAction): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, fetchScreen, (payload: ScreenRequestParams, options: RequestInit) => {
		return downloadScreenFromAPI(payload)
	})
}

export function downloadScreenFromAPI(params: ScreenRequestParams): Promise<Screen> {
	const { devicePlatform, screenId, screenVersion, accountId, language, deviceUdid } = params
	const shouldUsePublishedContent = getConfiguration().shouldUsePublishedContent()
	if (shouldUsePublishedContent) {
		return getConfiguration().stqryBunnyReadApi.fetchScreen(accountId || getConfiguration().getAccountId(), screenId, screenVersion, language)
	} else {
		return getApi().screensShow(devicePlatform, screenId, screenVersion, language,deviceUdid)
	}
}

/**
 * Fetches a single media item from api.
 * @param action contains request parameters
 */
function* handleFetchMediaItem(action: LoadMediaItemFromNetworkParams): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, fetchMediaItem, (payload: MediaItemRequestParams, options: RequestInit) => {
		return downloadMediaItemFromAPI(payload)
	})
}

/**
 * Downloads the media item json file from network. Exported function because we only use it for the Sync Content feature and do not need to persist media json files to disk,
 * only to extract the required information to download the actual media files.
 * @param params the params to perform the request
 */
export function downloadMediaItemFromAPI(params: MediaItemRequestParams): Promise<GetMediaItemsShowResponse> {
	const { devicePlatform, itemId, itemVersion, language, deviceUdid } = params
	const shouldUsePublishedContent = getConfiguration().shouldUsePublishedContent()
	if (shouldUsePublishedContent) {
		return getConfiguration().stqryBunnyReadApi.fetchMediaItem(getConfiguration().getAccountId(), itemId, itemVersion, language)
	} else {
		return getApi().mediaItemsShow(devicePlatform, itemId, itemVersion, language, deviceUdid)
	}
}

/**
 * Fetches a single form from api.
 * @param action contains request parameters
 */
function* handleFetchForm(action: LoadFormFromNetworkParams): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, fetchForm, (payload: FormRequestParams, options: RequestInit) => {
		const { id, version, language } = payload
		const shouldUsePublishedContent = getConfiguration().shouldUsePublishedContent()
		if (shouldUsePublishedContent) {
			return getConfiguration().stqryBunnyReadApi.fetchForm(getConfiguration().getAccountId(), id, version, language)
		} else {
			return getApi().formShow(id, version, language)
		}
	})
}

/**
 * Validates a short rental token and exchanges it for an app id and app token to be used to setup rental app
 * @param action contains request parameters
 */
function* handleValidateRentalToken(action: ValidateRentalTokenParams): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, validateRentalToken, (payload: RentalTokenParams, options: RequestInit) => {
		const { appId, token } = payload
		const params: ValidateToken = { short_token: token, token_type: ValidateToken.TokenTypeEnum.RentalMode }
		return getDirectApi().appToken(appId, params)
	})
}

/**
 * Validates a short rental token and exchanges it for an app id and app token to be used to setup rental app
 * @param action contains request parameters
 */
function* handleFetchManifest(action: LoadManifestFromNetworkParams): SagaIterator {
	const state = store.getState()
	const projectId = state.settings.appInfo.projectId

	// check if exists on Disk
	const manifest: Manifest | undefined = yield call(getConfiguration().loadAppManifestFromDisk, projectId)
	if (manifest) {
		// store onto redux state
		yield put(fetchManifestComplete(manifest))
	} else {
		// @ts-ignore
		yield call(callApi, action, fetchManifest, (payload: ManifestRequestParams, options: RequestInit) => {
			const shouldUsePublishedContent = getConfiguration().shouldUsePublishedContent()
			if (shouldUsePublishedContent) {
				return getConfiguration().stqryBunnyReadApi.fetchManifest(getConfiguration().getAccountId())
			} else {
				return getDirectApi().appManifest(payload.devicePlatform, payload.deviceUdid)
			}
	
		})
	}
}

/**
 * Fetches map toutes from api.
 * @param action contains request parameters
 */
function* handleFetchMapRoute(action: fetchMapRouteAction): SagaIterator {
	// @ts-ignore
	yield call(callApi, action, fetchMapRoute, (payload: MapRouteRequestParams, options: RequestInit) => {
		return downloadMapRouteFromAPI(payload)
	})
}

export function downloadMapRouteFromAPI(payload: MapRouteRequestParams): Promise<MapRoute> {
	const { devicePlatform, mapRouteVersion, language, mapRouteId, deviceUdid, accountId } = payload
	const shouldUsePublishedContent = getConfiguration().shouldUsePublishedContent()
	if (shouldUsePublishedContent) {
		return getConfiguration().stqryBunnyReadApi.fetchMapRoute(accountId || getConfiguration().getAccountId(), mapRouteId, mapRouteVersion, language)
	} else {
		return getApi().mapRouteShow(devicePlatform, mapRouteVersion, language, mapRouteId, deviceUdid)
	}
}

export default function*(): SagaIterator {
	// loads a single collection item (action contains item id)
	yield takeEvery(fetchScreen.started, handleFetchScreen)

	// loads a single collection (action contains collection id and version number)
	yield takeEvery(fetchCollectionItems.started, handleFetchCollectionItems)
	yield takeEvery(fetchCollection.started, handleFetchCollection)

	yield takeEvery(fetchMediaItem.started, handleFetchMediaItem)

	yield takeEvery(fetchForm.started, handleFetchForm)

	yield takeEvery(validateRentalToken.started, handleValidateRentalToken)
	yield takeEvery(fetchManifest.started, handleFetchManifest)

	yield takeEvery(fetchMapRoute.started, handleFetchMapRoute)

	// loads meta data for a given app id
	yield takeEvery(fetchAppMetaData.started, handleFetchAppMetaData)
	yield takeEvery(fetchAppMetaData.failed, handleFetchAppMetaDataFailed)

	yield takeEvery(validateCode.started, handleValidateCode)
	yield takeEvery(redeemCode.started, handleRedeemCode)
}
